import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Full-screen video modal with click-outside, ESC to close, and autoplay
 * (unmuted — opens in user gesture context so browsers allow audio).
 */
export const VideoModal = ({ open, onClose, src, title }) => {
  const videoRef = useRef(null);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = original;
    };
  }, [open, onClose]);

  // Pause + reset when closed
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (open) {
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={onClose}
          data-testid="video-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-6xl aspect-video bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              data-testid="video-modal-close"
              aria-label="Close video"
              className="absolute -top-4 -right-4 md:top-3 md:right-3 z-10 h-10 w-10 bg-white/95 hover:bg-copper hover:text-white text-ink flex items-center justify-center transition-colors shadow-lg"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Title strip (optional) */}
            {title && (
              <div className="absolute top-0 left-0 right-12 px-5 py-3 bg-gradient-to-b from-black/70 to-transparent z-[5]">
                <span className="overline text-copper">{title}</span>
              </div>
            )}

            <video
              ref={videoRef}
              src={src}
              controls
              playsInline
              preload="auto"
              className="w-full h-full object-contain bg-black"
              data-testid="video-modal-player"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
